import { useCallback, useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type { ApiClient, Projekt } from '../api';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe,
  LoadingState, StatusBadge, type Spalte,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

/**
 * Die Gewerkekürzel im Klartext.
 *
 * In einer Tabelle, in die jemand Namen und Adressen eintragen soll, ist 'klt'
 * eine Zumutung: Wer 'Kälte' liest, weiss, welchen Kollegen er sucht.
 */
const GEWERK: Record<string, string> = {
  rlt: 'Lüftung', hzg: 'Heizung', klt: 'Kälte', san: 'Sanitär',
  spr: 'Sprinkler', elt: 'Elektro',
};

/**
 * Der nächtliche Lauf — Modellstände, Tagesberichte, Güte der Vorschläge.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DER KERN IST NICHT DER LAUF, SONDERN DAS SCHWEIGEN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *     Modellstand unverändert?  →  KEIN Lauf, KEINE Meldung
 *
 * Eine tägliche Meldung ohne Inhalt trainiert das Wegklicken — und danach wird
 * auch die nicht gelesen, die etwas enthielt.
 *
 * Deshalb steht auf dieser Seite an erster Stelle, wie viele Planer NICHTS
 * bekommen. Das ist die Zahl, an der man sieht, ob die Automatisierung
 * funktioniert.
 */
export function Nachtlauf() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [modelle, setModelle] = useState<any | null>(null);
  const [bericht, setBericht] = useState<any | null>(null);
  const [guete, setGuete] = useState<any | null>(null);
  const [zust, setZust] = useState<any | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [alle, setAlle] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);

  const laden = useCallback(() => {
    if (!projekt) return;

    api.koordinationZustaendigkeiten(projekt.id)
      .then(setZust).catch(() => setZust(null));

    api.koordinationModellstaende(projekt.id)
      .then(setModelle).catch(() => setModelle(null));

    api.koordinationTagesbericht(projekt.id)
      .then(setBericht)
      .catch(setFehler);

    api.koordinationVorschlagsguete(projekt.id)
      .then(setGuete).catch(() => setGuete(null));
  }, [api, projekt]);

  useEffect(() => { laden(); }, [laden]);

  const freischalten = async (a: any, frei: boolean) => {
    if (!projekt) return;

    setMeldung(null);

    try {
      const e: any = await api.koordinationFreischalten(projekt.id, {
        gewerk: a.gewerk, gegenGewerk: a.gegenGewerk,
        richtung: a.richtung, frei,
      });

      setMeldung(e?.meldung ?? null);
      laden();
    } catch (x) {
      setMeldung(x instanceof Error ? x.message : String(x));
    }
  };

  /**
   * Einen Planer hinterlegen.
   *
   * Nach dem Speichern wird NUR die Zuständigkeitstabelle neu geholt und nicht
   * die ganze Seite: Der Tagesbericht rechnet über 500.000 Kollisionen, und ihn
   * bei jedem eingetippten Namen neu zu rechnen wäre eine halbe Minute Wartezeit
   * pro Feld.
   *
   * Der Bericht ändert sich dadurch trotzdem — er zeigt den neuen Empfänger
   * erst beim nächsten Laden. Das ist der Grund für die Meldung: Sie sagt, dass
   * gespeichert wurde, damit niemand am Bericht darunter nachsieht und denkt,
   * es sei nichts angekommen.
   */
  const setzen = async (
    r: any, w: { planerName?: string | null; planerMail?: string | null },
  ) => {
    if (!projekt) return;

    // Nichts geändert -- kein Schreibvorgang. Ohne diese Abfrage schreibt jedes
    // Anklicken eines Feldes, auch wenn nur gelesen wurde.
    if ((w.planerName ?? '') === (r.planerName ?? '')
     && (w.planerMail ?? '') === (r.planerMail ?? '')) return;

    setMeldung(null);

    try {
      await api.koordinationZustaendigkeitSetzen(projekt.id, r.id, w);

      const frisch = await api.koordinationZustaendigkeiten(projekt.id);

      setZust(frisch);
      setMeldung(
        `${r.bereich}, ${GEWERK[r.gewerk] ?? r.gewerk}: gespeichert. Der `
      + 'Tagesbericht darunter zeigt den Empfänger beim nächsten Laden.');
    } catch (x) {
      setMeldung(x instanceof Error ? x.message : String(x));
    }
  };

  const kopfzeile = (
    <PageHeader
      titel="Der nächtliche Lauf"
      beschreibung={'Ein Planer, der morgens seine drei neuen Aufgaben vorfindet, '
        + 'arbeitet damit — einer, der einmal im Monat 400 bekommt, nicht. Und wessen '
        + 'Bereich sich nicht geändert hat, bekommt nichts: Eine tägliche Meldung ohne '
        + 'Inhalt trainiert das Wegklicken.'}
      aktionen={projekt && (
        <Link to={`/projekte/${projekt.id}/koordination`} className="m-knopf" style={{ textDecoration: 'none' }}>
          Koordination
        </Link>
      )}
    />
  );

  if (!projekt) {
    return (
      <>
        {kopfzeile}
        <LoadingState text="Projekt wird geladen …" zeilen={5} />
      </>
    );
  }

  const berichte = ((bericht?.berichte ?? []) as any[])
    .filter((b) => alle || b.lohnt);

  /*
    Gespeichert wird beim VERLASSEN des Feldes und nicht bei jedem
    Tastendruck: 36 Zeilen mal jeder Buchstabe wären hunderte
    Schreibvorgänge, und ein halb getippter Name stünde zwischendurch als
    gültiger Wert in der Tabelle. `Feld` meldet bei jedem Tastendruck -
    deshalb bleibt hier der Zwischenspeicher der Zeile und der Schreibvorgang
    am `onBlur` des umschliessenden Elements.
  */
  const zustaendigSpalten: Spalte<any>[] = [
    {
      schluessel: 'abschnitt', titel: 'Abschnitt', breite: '160px',
      immerSichtbar: true,
      wert: (r) => r.bereich,
      inhalt: (r) => (
        <>
          {r.bereich}
          <div className="schwach" style={{ fontSize: 11 }}>{r.quadrant}</div>
        </>
      ),
    },
    {
      schluessel: 'gewerk', titel: 'Gewerk', breite: '130px',
      wert: (r) => GEWERK[r.gewerk] ?? r.gewerk,
    },
    {
      schluessel: 'planer', titel: 'Planer', sortierbar: false,
      wert: (r) => r.planerName ?? '',
      inhalt: (r) => (
        <PlanerEingabe
          beschriftung={`Planer für ${r.bereich}, ${GEWERK[r.gewerk] ?? r.gewerk}`}
          wert={r.planerName ?? ''}
          beiFertig={(w) => setzen(r, { planerName: w, planerMail: r.planerMail })}
          platzhalter="Name"
        />
      ),
    },
    {
      schluessel: 'mail', titel: 'E-Mail', sortierbar: false,
      wert: (r) => r.planerMail ?? '',
      inhalt: (r) => (
        <PlanerEingabe
          beschriftung={`E-Mail für ${r.bereich}, ${GEWERK[r.gewerk] ?? r.gewerk}`}
          wert={r.planerMail ?? ''}
          beiFertig={(w) => setzen(r, { planerName: r.planerName, planerMail: w })}
          platzhalter="leer lassen ist in Ordnung"
        />
      ),
    },
  ];

  const gueteSpalten: Spalte<any>[] = [
    {
      schluessel: 'gewerk', titel: 'Gewerk', immerSichtbar: true,
      wert: (a2) => a2.gewerk ?? '',
      inhalt: (a2) => a2.gewerk ?? '—',
    },
    {
      schluessel: 'gegen', titel: 'gegen',
      wert: (a2) => a2.gegenGewerk ?? '',
      inhalt: (a2) => a2.gegenGewerk ?? '—',
    },
    { schluessel: 'richtung', titel: 'Richtung', wert: (a2) => a2.richtung },
    { schluessel: 'vorschlaege', titel: 'Vorschläge', art: 'zahl', wert: (a2) => a2.vorschlaege },
    { schluessel: 'entschieden', titel: 'entschieden', art: 'zahl', wert: (a2) => a2.entschieden },
    {
      schluessel: 'annahme', titel: 'Annahme', art: 'zahl', einheit: '%',
      breite: '150px',
      wert: (a2) => (a2.annahmequote != null ? a2.annahmequote * 100 : null),
      inhalt: (a2) => {
        if (a2.annahmequote == null) return '—';
        const text = `${(a2.annahmequote * 100).toFixed(0)} %`;
        if (a2.regelpflegeMelden) {
          return <StatusBadge zustand="fehler" text={text} klein
                              titel="Über der Ablehnschwelle" />;
        }
        if (a2.reifFuerAutomatik) {
          return <StatusBadge zustand="gut" text={text} klein
                              titel="Reif für die automatische Übernahme" />;
        }
        return <span>{text}</span>;
      },
    },
    {
      schluessel: 'befund', titel: 'Befund',
      wert: (a2) => a2.befund,
      inhalt: (a2) => (
        <span style={{ fontSize: 12 }}>
          {a2.befund}
          {(a2.ablehngruende ?? []).length > 0 && (
            <div className="schwach" style={{ fontSize: 11, marginTop: 3 }}>
              {(a2.ablehngruende as any[])
                .map((g: any) => `${g.text} (${g.anzahl})`)
                .join(' · ')}
            </div>
          )}
        </span>
      ),
    },
    {
      schluessel: 'aktion', titel: '', breite: '150px', sortierbar: false,
      ausrichtung: 'rechts',
      inhalt: (a2) => {
        if (a2.istFreigegeben) {
          return (
            <button className="m-knopf" onClick={() => freischalten(a2, false)}>
              zurücknehmen
            </button>
          );
        }
        if (a2.reifFuerAutomatik) {
          return (
            <button className="m-knopf m-knopf-haupt" onClick={() => freischalten(a2, true)}>
              freischalten
            </button>
          );
        }
        return null;
      },
    },
  ];

  return (
    <>
      {kopfzeile}

      {fehler !== null && (
        <ErrorState
          meldung="Der Tagesbericht konnte nicht gerechnet werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      )}
      {meldung && <div className="hinweis">{meldung}</div>}

      {/* ══════════════════════════════════════════════════════════════════
          WELCHES MODELL SICH GEAENDERT HAT
          ══════════════════════════════════════════════════════════════════ */}
      {/* Nur zeigen, wenn der Endpunkt wirklich etwas geliefert hat: Ein
          Abschnitt mit Überschrift und leerer Fläche sieht aus, als wäre etwas
          kaputt. */}
      {modelle && (modelle.grund || (modelle.modelle ?? []).length > 0) && (
        <ContentSection titel="Modellstände">
          <p style={{ fontSize: 13, marginTop: 0 }}>
            {modelle.veraendert === false
              ? <StatusBadge zustand="gut" text={modelle.grund} />
              : modelle.grund}
          </p>

          {(modelle.modelle ?? []).length > 0 && (
            <DataTable
              spalten={[
                {
                  schluessel: 'modell', titel: 'Herkunftsmodell', immerSichtbar: true,
                  wert: (m: any) => m.herkunftsmodell,
                },
                {
                  schluessel: 'bauteile', titel: 'Bauteile', art: 'zahl',
                  wert: (m: any) => m.bauteile,
                },
                {
                  schluessel: 'vorlauf', titel: 'gegen Vorlauf', art: 'status',
                  breite: '170px',
                  wert: (m: any) => (m.istVeraendert === true ? 'geändert'
                    : m.istVeraendert === false ? 'unverändert' : 'kein Vorlauf'),
                  inhalt: (m: any) => {
                    if (m.istVeraendert === true) {
                      return <StatusBadge zustand="warnung" text="geändert" klein />;
                    }
                    if (m.istVeraendert === false) {
                      return <StatusBadge zustand="gut" text="unverändert" klein />;
                    }
                    return <StatusBadge zustand="neutral" text="kein Vorlauf" klein />;
                  },
                },
                {
                  schluessel: 'pruefsumme', titel: 'Prüfsumme', breite: '160px',
                  sortierbar: false,
                  wert: (m: any) => String(m.pruefsumme ?? ''),
                  inhalt: (m: any) => (
                    <span className="schwach"
                          style={{ fontFamily: 'monospace', fontSize: 11 }}
                          title={String(m.pruefsumme ?? '')}>
                      {String(m.pruefsumme ?? '').slice(0, 12)}…
                    </span>
                  ),
                },
              ]}
              zeilen={modelle.modelle as any[]}
              schluessel={(m: any) => m.herkunftsmodell}
              suche={false}
              spaltenwahl={false}
              dicht
            />
          )}

          {(modelle.hinweise ?? []).map((h: string, i: number) => (
            <p key={i} className="schwach" style={{ fontSize: 12 }}>{h}</p>
          ))}
        </ContentSection>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          WER ZUSTÄNDIG IST
          ══════════════════════════════════════════════════════════════════

          Steht VOR den Tagesberichten, weil sie ohne diese Tabelle keinen
          Empfänger haben. Ein Bericht, der gerechnet und nicht zugestellt
          wird, sieht auf der Seite darunter genauso aus wie einer, der
          hinausgeht — und der Unterschied entscheidet, ob jemand etwas tut. */}
      {zust && (
        <ContentSection
          titel="Zuständig je Abschnitt und Gewerk"
          beschreibung={zust.hinweis}
        >
          {zust.ohneAdresse > 0 && (
            <KpiReihe>
              <KpiCard
                titel="Ohne Empfänger" wert={zust.ohneAdresse}
                zustand="warnung"
                zustandText="der Bericht wird gerechnet, aber nicht zugestellt"
              />
              <KpiCard titel="Besetzt" wert={zust.gesamt - zust.ohneAdresse} />
            </KpiReihe>
          )}

          <DataTable
            spalten={zustaendigSpalten}
            zeilen={(zust.zeilen ?? []) as any[]}
            schluessel={(r: any) => r.id}
            suchePlatzhalter="Abschnitt, Gewerk, Planer …"
            spaltenwahl={false}
          />
        </ContentSection>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          DIE TAGESBERICHTE
          ══════════════════════════════════════════════════════════════════ */}
      {bericht && (
        <ContentSection
          titel="Tagesberichte"
          aktionen={(
            <button className="m-knopf" onClick={() => setAlle(!alle)}>
              {alle ? 'nur mit Inhalt' : 'auch die ohne Inhalt'}
            </button>
          )}
        >
          <KpiReihe>
            <KpiCard titel="Mit Inhalt" wert={bericht.empfaenger} />
            {/* Diese Zahl ist die wichtige: Sie zeigt, dass die Automatisierung
                schweigt, wenn es nichts zu sagen gibt. */}
            <KpiCard
              titel="Bekommen nichts" wert={bericht.ohneInhalt}
              vergleich="Modellstand unverändert — keine Meldung"
            />
          </KpiReihe>

          {berichte.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {berichte.map((b: any, i: number) => (
                <div key={i}
                     style={{ borderLeft: `3px solid ${
                       b.regressionen > 0 ? 'var(--schlecht)'
                         : b.lohnt ? 'var(--laufend)' : 'var(--linie)'}`,
                       paddingLeft: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    Bereich {b.bereich ?? '—'}, {b.gewerk}
                    <span className="schwach"
                          style={{ fontWeight: 400, marginLeft: 8 }}>
                      {b.planerName ?? 'kein Planer gepflegt'}
                      {b.planerMail ? ` · ${b.planerMail}` : ''}
                    </span>
                  </div>

                  <div style={{ fontSize: 13, marginTop: 2 }}>{b.zeile}</div>

                  {b.regressionen > 0 && (
                    <div style={{ marginTop: 3 }}>
                      <StatusBadge
                        zustand="fehler" klein
                        text="Regression — schon entschieden und zurück"
                        titel="Die dringendste Sorte Aufgabe."
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {berichte.length === 0 && (
            <EmptyState
              titel={alle ? 'Keine Tagesberichte.' : 'Kein Bericht mit Inhalt.'}
              erklaerung={alle
                ? 'Es gibt keine Zuständigkeit, für die ein Bericht entstehen könnte.'
                : 'Kein Bereich hat sich geändert — genau dann schweigt der Lauf.'}
              aktion={!alle ? (
                <button className="m-knopf" onClick={() => setAlle(true)}>
                  Auch die ohne Inhalt zeigen
                </button>
              ) : undefined}
            />
          )}

          {(bericht.hinweise ?? []).map((h: string, i: number) => (
            <p key={i} className="schwach" style={{ fontSize: 12 }}>{h}</p>
          ))}
        </ContentSection>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          DIE GUETE DER VORSCHLAEGE — UND BEIDE RICHTUNGEN
          ══════════════════════════════════════════════════════════════════ */}
      {guete && (
        <ContentSection
          titel="Güte der Vorschläge"
          beschreibung={`Über ${(guete.mindestquote * 100).toFixed(0)} % Annahme über `
            + `mindestens ${guete.mindestfaelle} Fälle → zur automatischen Übernahme `
            + `anbieten. Über ${(guete.ablehnschwelle * 100).toFixed(0)} % Ablehnung → `
            + 'die Regel bildet die Wirklichkeit nicht ab, und das ist eine Sache der '
            + 'Regelpflege, nicht der Planer.'}
        >
          <KpiReihe>
            <KpiCard titel="Vorschlagsarten" wert={guete.arten} />
            <KpiCard
              titel="Reif für Automatik" wert={guete.reifFuerAutomatik}
              zustand={guete.reifFuerAutomatik > 0 ? 'gut' : undefined}
              zustandText={guete.reifFuerAutomatik > 0 ? 'freischaltbar' : undefined}
            />
            <KpiCard titel="Freigeschaltet" wert={guete.freigegeben} />
            <KpiCard
              titel="In die Regelpflege" wert={guete.regelpflegeMelden}
              zustand={guete.regelpflegeMelden > 0 ? 'fehler' : undefined}
              zustandText={guete.regelpflegeMelden > 0
                ? 'die Regel, nicht der Planer' : undefined}
            />
          </KpiReihe>

          {(guete.liste ?? []).length > 0 && (
            <DataTable
              spalten={gueteSpalten}
              zeilen={guete.liste as any[]}
              schluessel={(a2: any) =>
                `${a2.gewerk ?? ''}|${a2.gegenGewerk ?? ''}|${a2.richtung}`}
              ausfuhr
              ausfuhrDateiname="Vorschlagsguete"
              sortierung={{ spalte: 'annahme', richtung: 'ab' }}
            />
          )}

          {(guete.hinweise ?? []).map((h: string, i: number) => (
            <p key={i} className="schwach" style={{ fontSize: 12 }}>{h}</p>
          ))}
        </ContentSection>
      )}

    </>
  );
}

/**
 * Ein Namens- oder Adressfeld der Zuständigkeitstabelle.
 *
 * `Feld` aus der Musterbibliothek meldet bei jedem Tastendruck; geschrieben
 * wird hier aber erst beim VERLASSEN des Feldes (36 Zeilen mal jeder
 * Buchstabe wären hunderte Schreibvorgänge, und ein halb getippter Name
 * stünde zwischendurch als gültiger Wert in der Tabelle). Deshalb hält diese
 * kleine Komponente den Zwischenstand und meldet ihn einmal.
 *
 * Die Beschriftung ist für Vorlesehilfen da und steht nicht sichtbar in der
 * Zelle - die Spaltenüberschrift sagt sie schon (Abschnitt 28).
 */
function PlanerEingabe({
  beschriftung, wert, beiFertig, platzhalter,
}: {
  beschriftung: string;
  wert: string;
  beiFertig: (wert: string) => void;
  platzhalter?: string;
}) {
  return (
    <>
      <label className="m-nur-vorlesen" htmlFor={`planer-${beschriftung}`}>
        {beschriftung}
      </label>
      <input
        id={`planer-${beschriftung}`}
        defaultValue={wert}
        placeholder={platzhalter}
        style={{ width: '100%', fontSize: 12 }}
        onClick={(e) => e.stopPropagation()}
        onBlur={(e) => beiFertig(e.target.value)}
      />
    </>
  );
}
